/**
 * SUPERCRUISE WARP VISUAL: a distinct star-stretch overlay that activates ONLY
 * while supercruise is engaged. Renders long radial streaks converging toward
 * the camera's facing direction — the classic "warp tunnel" read — entirely
 * separate from the normal speed-lines (which are suppressed in supercruise).
 *
 * Design choices:
 *  - The object is PARENTED UNDER THE CAMERA so it inherits both position AND
 *    rotation, keeping the tunnel aligned with where the player looks / ship flies.
 *    No per-frame position copy is needed; streaks live in camera-local space.
 *  - Streak cone centre points toward -Z (camera forward in Three.js convention).
 *  - Intensity uses a LOG mapping keyed to a VISUAL_REF_SPEED so the effect is
 *    near-full by ~50,000 km/s (a realistic cruise speed) rather than the previous
 *    linear mapping against OPEN_SPACE_MAX=2,000,000 which was invisible at play.
 *  - Colour: deep blue-white (0xadd8ff) vs the cyan of normal speed-lines (0x9fe6f0).
 *  - Additive blending, depthWrite=false — purely additive screen-space overlay.
 *  - No Math.random at runtime: layout seeded deterministically at construction.
 *  - Client-only, replay-safe: reads only render scalars, never writes sim-core.
 *  - Cleaned up (fade to zero) when cruise drops to NORMAL so no artifact lingers.
 */
import * as THREE from 'three';

const STREAK_COUNT = 160;
/** Half-angle (radians) of the forward cone within which streaks originate. */
const CONE_HALF_ANGLE = Math.PI * 0.38; // ~68° — wide enough to fill peripheral vision
/** Distance (km, camera-relative) at which each streak sits. */
const STREAK_DIST = 180;
/** Min/max streak length at zero / full intensity (km). */
const LEN_MIN = 4;
const LEN_MAX = 55;
/** Radial expansion speed (km/s perceptual) — streaks drift outward each frame. */
const RADIAL_DRIFT = 90;
/** Colour: cold blue-white, distinguishable from speed-lines cyan. */
const WARP_COLOUR = 0xadd8ff;
/** Opacity at full intensity. */
const OPACITY_MAX = 0.65;

/**
 * LOG-CURVE reference speed (km/s). At this exact speed the log fraction = 1.0
 * (full intensity). Any speed above this clamps at 1.0. Chosen so that typical
 * in-game cruise speeds (tens-to-hundreds-of-thousands km/s) give near-max warp.
 *
 * Calibration:
 *   speedFrac = clamp(log(1 + speedKmS / VISUAL_REF_SPEED) / log(2), 0, 1)
 *   @  50,000 km/s → log(1 + 50000/50000)/log(2) = log(2)/log(2) = 1.00  (full)
 *   @ 500,000 km/s → log(11)/log(2)                               ≈ 3.46 → 1.00
 *   @  20,000 km/s → log(1.4)/log(2)                              ≈ 0.485
 *   @   5,000 km/s → log(1.1)/log(2)                              ≈ 0.137
 *   (spool at 0 km/s → 0 — gentle fade-in preserved via spoolFrac gate below)
 */
const VISUAL_REF_SPEED = 50_000; // km/s — full intensity by this speed
const LOG2 = Math.LN2; // Math.log(2), reused each frame

/** Deterministic hash [0,1) from integer seed — no runtime Math.random. */
const hash = (n: number): number => {
  const s = Math.sin(n * 127.1) * 43758.5453;
  return s - Math.floor(s);
};

/**
 * Uniform sample of a direction inside the FORWARD cone in camera-local space.
 * Camera forward = -Z in Three.js, so the cone centre is (0, 0, -1).
 */
const coneDir = (i: number): THREE.Vector3 => {
  // Spherical-cap sampling: azimuth uniform in [0,2π], cos(polar) in [cos(half),1]
  const azimuth = hash(i * 7 + 1) * Math.PI * 2;
  const cosMin = Math.cos(CONE_HALF_ANGLE);
  const cosTheta = cosMin + hash(i * 7 + 3) * (1 - cosMin);
  const sinTheta = Math.sqrt(1 - cosTheta * cosTheta);
  return new THREE.Vector3(
    Math.cos(azimuth) * sinTheta,
    Math.sin(azimuth) * sinTheta,
    -cosTheta, // -Z = camera forward in Three.js local space
  );
};

export class SupercruiseWarp {
  readonly object: THREE.LineSegments;
  private readonly positions: Float32Array;
  private readonly geo: THREE.BufferGeometry;
  private readonly mat: THREE.LineBasicMaterial;
  /** Per-streak radial offset (0..1 phase, wraps each frame). */
  private readonly phase: Float32Array;
  /** Per-streak stable cone direction (unit vector, camera-local). */
  private readonly dirs: Float32Array; // COUNT * 3

  constructor() {
    this.phase = new Float32Array(STREAK_COUNT);
    this.dirs = new Float32Array(STREAK_COUNT * 3);
    for (let i = 0; i < STREAK_COUNT; i++) {
      this.phase[i] = hash(i * 3 + 99); // stagger so they don't all wrap at once
      const d = coneDir(i);
      this.dirs[i * 3] = d.x;
      this.dirs[i * 3 + 1] = d.y;
      this.dirs[i * 3 + 2] = d.z;
    }

    this.positions = new Float32Array(STREAK_COUNT * 6); // 2 verts × 3 floats
    this.geo = new THREE.BufferGeometry();
    this.geo.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    this.mat = new THREE.LineBasicMaterial({
      color: WARP_COLOUR,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.object = new THREE.LineSegments(this.geo, this.mat);
    this.object.frustumCulled = false;
    this.object.renderOrder = 3; // above speed-lines (renderOrder 2)
    // NOTE: caller must do camera.add(this.object) — NOT scene.add — so the object
    // inherits the camera's world rotation and the tunnel points where the player looks.
  }

  /**
   * Update the warp overlay each frame.
   * The object is parented under the camera, so no position copy is needed —
   * position is implicitly (0,0,0) in camera-local space.
   *
   * @param dt           frame seconds
   * @param cruiseActive whether supercruise is engaged (SPOOLING or SUPERCRUISE)
   * @param speedKmS     current supercruise speed (km/s) — drives intensity
   * @param spoolFrac    0..1 spool progress — fades in during spool
   */
  update(
    dt: number,
    cruiseActive: boolean,
    speedKmS: number,
    spoolFrac: number,
  ): void {
    if (!cruiseActive) {
      this.mat.opacity = 0;
      this.object.visible = false;
      return;
    }

    // Perceptual log-curve intensity: reaches 1.0 at VISUAL_REF_SPEED (50,000 km/s).
    // Multiply by spool fraction for a gentle fade-in at engage time.
    const rawFrac = Math.log(1 + speedKmS / VISUAL_REF_SPEED) / LOG2;
    const speedFrac = Math.min(1, rawFrac);
    // During spool: base glow = 0.15 ensures the tunnel is visible immediately;
    // grows to full with both spool completion AND speed.
    const intensity = spoolFrac * (0.15 + 0.85 * speedFrac);

    this.mat.opacity = Math.min(OPACITY_MAX, intensity * OPACITY_MAX);
    this.object.visible = intensity > 0.01;

    const lenKm = LEN_MIN + (LEN_MAX - LEN_MIN) * speedFrac;
    const driftStep = RADIAL_DRIFT * dt;

    this.writeStreaks(lenKm, driftStep);
  }

  /** Advance radial phase and write segment pair for each streak. */
  private writeStreaks(lenKm: number, driftStep: number): void {
    for (let i = 0; i < STREAK_COUNT; i++) {
      // Phase advances outward; wrap at 1 to keep streaks inside the sphere.
      this.phase[i] = (this.phase[i]! + driftStep / STREAK_DIST) % 1;
      const p = this.phase[i]!;

      // Radial distance: each streak occupies a segment between p*DIST and (p+len)*DIST.
      const rNear = p * STREAK_DIST;
      const rFar = Math.min(rNear + lenKm, STREAK_DIST);

      const dx = this.dirs[i * 3]!;
      const dy = this.dirs[i * 3 + 1]!;
      const dz = this.dirs[i * 3 + 2]!;

      const o = i * 6;
      this.positions[o] = dx * rNear;
      this.positions[o + 1] = dy * rNear;
      this.positions[o + 2] = dz * rNear;
      this.positions[o + 3] = dx * rFar;
      this.positions[o + 4] = dy * rFar;
      this.positions[o + 5] = dz * rFar;
    }
    (this.geo.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
  }
}
